import { useEffect, useState } from "react";
import { Alert, Box, Chip, CircularProgress, Dialog, DialogContent, DialogTitle, IconButton, Typography } from "@mui/material";
import { X } from "lucide-react";
import swapSetsApi from "../../api/swapSets";

interface SwapItem { _id: string; name: string; imageUrl?: string; }
interface DaySwapSet {
  serialNumber: number;
  swapSet: null | {
    active: boolean;
    meals: { mealId: SwapItem | null; active: boolean }[];
    drinks: { drinkId: SwapItem | null; active: boolean; isDefault?: boolean }[];
  };
}

export default function DaySwapSetDialog({ open, onClose, date }: { open: boolean; onClose: () => void; date: Date }) {
  // Match the calendar day displayed in Order Day Explorer without UTC shifting it.
  const day = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
  const [data, setData] = useState<DaySwapSet | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true); setData(null); setError("");
    swapSetsApi.getSwapSetForDate(day).then(result => {
      if (!cancelled) setData(result);
    }).catch(() => {
      if (!cancelled) setError("Could not load this day's swap set.");
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, day, retry]);

  const section = (title: string, entries: { item: SwapItem | null; active: boolean; isDefault?: boolean }[]) => (
    <Box sx={{ mt: 3 }}>
      <Typography variant="subtitle1" fontWeight={600}>{title} ({entries.length})</Typography>
      {!entries.length && <Typography color="text.secondary" sx={{ mt: 1 }}>No {title.toLowerCase()} configured.</Typography>}
      {entries.map((entry, index) => (
        <Box key={entry.item?._id || index} sx={{ display: "flex", alignItems: "center", gap: 2, py: 1.5, borderBottom: "1px solid", borderColor: "divider" }}>
          {entry.item?.imageUrl && <Box component="img" src={entry.item.imageUrl} alt="" sx={{ width: 48, height: 48, borderRadius: 1, objectFit: "cover" }} />}
          <Typography sx={{ flex: 1 }}>{entry.item?.name || "Item no longer available"}</Typography>
          {entry.isDefault && <Chip label="Default" size="small" variant="outlined" />}
          <Chip label={entry.active && data?.swapSet?.active && entry.item ? "Active" : "Inactive"} color={entry.active && data?.swapSet?.active && entry.item ? "success" : "default"} size="small" />
        </Box>
      ))}
    </Box>
  );
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" aria-labelledby="day-swap-set-title">
      <DialogTitle id="day-swap-set-title" sx={{ pr: 7 }}>
        {data ? `Swap set ${data.serialNumber}` : "Daily swap set"}
        <Typography variant="body2" color="text.secondary">{day}</Typography>
        <IconButton aria-label="Close swap set" onClick={onClose} sx={{ position: "absolute", right: 12, top: 12 }}><X size={20} /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading && <Box sx={{ textAlign: "center", py: 4 }}><CircularProgress size={28} aria-label="Loading swap set" /></Box>}
        {error && <Alert severity="error" action={<button onClick={() => setRetry(value => value + 1)}>Retry</button>}>{error}</Alert>}
        {data && !data.swapSet && <Alert severity="info">Swap set {data.serialNumber} has not been configured for this date.</Alert>}
        {data?.swapSet && <>
          {!data.swapSet.active && <Alert severity="warning">This swap set is inactive.</Alert>}
          {section("Meals", (data.swapSet.meals || []).map(entry => ({ ...entry, item: entry.mealId })))}
          {section("Drinks", (data.swapSet.drinks || []).map(entry => ({ ...entry, item: entry.drinkId })))}
        </>}
      </DialogContent>
    </Dialog>
  );
}
